import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Button from '../../../components/Button';
import AdminModal from '../../components/AdminModal';
import { WarningIcon, SuccessIcon } from '../../../components/icons';
import { HelpTrigger } from '../../../help';
import { apiError, labelClasses } from '../elections/InitiativeDetailPage';
import { coverageLabel } from '../../../constants/candidateProfiles';
import {
  importProfile, pickBundleFiles, preflightProfile,
  type ProfileBundle, type ProfilePreflight,
} from '../../lib/candidateProfiles';

interface Props {
  candidateId: string;
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful import so the cards refresh their queries. */
  onImported: () => void;
}

const ACCEPT = '.json,.pdf,application/json,application/pdf';

/**
 * Import a candidate profile bundle — one candidacy (CANDIDATE_PROFILES_PRD.md
 * §5.4.1). Pick the skill's files (one dialog, one to three files), see exactly
 * what will be replaced, keep any field you would rather not overwrite, then
 * import. The server refuses a bundle for a different candidate, the wrong
 * election type, a different tier from the race, or one with open
 * [NEEDS SOURCE] gaps; the preflight shows that before the button is live.
 */
const CandidateProfileImportModal: React.FC<Props> = ({ candidateId, isOpen, onClose, onImported }) => {
  const [bundle, setBundle] = useState<ProfileBundle>({ fields: null, record: null, pdf: null });
  const [keep, setKeep] = useState<Set<string>>(new Set());
  const [preflight, setPreflight] = useState<ProfilePreflight | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setBundle({ fields: null, record: null, pdf: null });
    setKeep(new Set());
    setPreflight(null);
    setError(null);
  };
  const close = () => { reset(); onClose(); };

  const onFiles = (files: FileList | null) => {
    const picked = pickBundleFiles(Array.from(files ?? []));
    setBundle(picked);
    setPreflight(null);
    setError(picked.fields ? null : 'Include the *-fields.json file — it is the one that carries the content.');
  };

  const check = useMutation({
    mutationFn: () => preflightProfile(candidateId, bundle, [...keep]),
    onSuccess: (result) => { setPreflight(result); setError(null); },
    onError: (err: any) => setError(apiError(err, 'Could not read the profile files')),
  });

  const run = useMutation({
    mutationFn: () => importProfile(candidateId, bundle, [...keep]),
    onSuccess: (summary) => {
      toast.success(
        summary.publication_status === 'published'
          ? `Imported ${summary.version ?? 'profile'} — live for citizens now`
          : `Imported ${summary.version ?? 'profile'} — this profile still needs publishing`
      );
      onImported();
      close();
    },
    onError: (err: any) => {
      setError(apiError(err, 'Import failed'));
      if (err?.response?.data?.data?.blocking) setPreflight(err.response.data.data as ProfilePreflight);
    },
  });

  const toggleKeep = (column: string) => {
    setKeep((current) => {
      const next = new Set(current);
      if (next.has(column)) next.delete(column); else next.add(column);
      return next;
    });
    setPreflight((current) => current && ({
      ...current,
      fields: current.fields.map((f) => (f.column === column
        ? { ...f, action: keep.has(column) ? (f.incoming ? 'replace' : (f.current ? 'clear' : 'empty')) : 'keep' }
        : f)),
    }));
  };

  const canImport = Boolean(preflight?.ok) && !run.isPending;

  return (
    <AdminModal
      isOpen={isOpen}
      onClose={close}
      title="Import candidate profile"
      size="lg"
      footer={<>
        <Button variant="secondary" size="sm" onClick={close}>Cancel</Button>
        {!preflight && (
          <Button variant="primary" size="sm" onClick={() => check.mutate()} disabled={!bundle.fields || check.isPending} loading={check.isPending}>
            Preview
          </Button>
        )}
        {preflight && (
          <Button variant="primary" size="sm" onClick={() => run.mutate()} disabled={!canImport} loading={run.isPending}>
            Import
          </Button>
        )}
      </>}
    >
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Pick this candidate&apos;s profile files from the skill&apos;s output — the
            <code className="mx-1">-fields.json</code> file is required; the record JSON and PDF
            are attached as supporting documents when included.
          </p>
          <HelpTrigger contentKey="candidate-profile-import" size="sm" ariaLabel="Help for importing a candidate profile" />
        </div>

        <div>
          <label htmlFor="profile-files" className={labelClasses}>Profile files</label>
          <input
            id="profile-files"
            type="file"
            multiple
            accept={ACCEPT}
            className="input w-full"
            onChange={(e) => onFiles(e.target.files)}
          />
          {(bundle.fields || bundle.record || bundle.pdf) && (
            <ul className="mt-2 space-y-0.5 text-xs text-text-tertiary dark:text-text-dark-tertiary">
              <li>Fields: {bundle.fields?.name ?? <span className="text-danger">missing</span>}</li>
              <li>Record: {bundle.record?.name ?? 'not included'}</li>
              <li>PDF: {bundle.pdf?.name ?? 'not included'}</li>
            </ul>
          )}
        </div>

        {error && (
          <p className="rounded-lg bg-danger-light px-3 py-2 text-sm text-danger dark:bg-danger-dark/20">{error}</p>
        )}

        {preflight && (
          <div className="space-y-3">
            {preflight.blocking.map((reason) => (
              <p key={reason} className="flex items-start gap-2 rounded-lg bg-danger-light px-3 py-2 text-sm text-danger dark:bg-danger-dark/20">
                <WarningIcon className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{reason}</span>
              </p>
            ))}
            {preflight.warnings.map((warning) => (
              <p key={warning} className="flex items-start gap-2 rounded-lg bg-warning-100 px-3 py-2 text-sm text-warning-700 dark:bg-warning-900/20 dark:text-warning-300">
                <WarningIcon className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{warning}</span>
              </p>
            ))}

            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <dt className="text-text-secondary dark:text-text-dark-secondary">Candidate</dt>
              <dd className="text-text-primary dark:text-text-dark-primary">
                {preflight.candidate.name}
                <span className="ml-1 text-text-tertiary dark:text-text-dark-tertiary">
                  · {preflight.candidate.contest_label ?? preflight.candidate.office_sought ?? '—'}
                  {preflight.candidate.contest_id ? '' : ' · not linked to a contest'}
                </span>
              </dd>
              <dt className="text-text-secondary dark:text-text-dark-secondary">Version</dt>
              <dd className="text-text-primary dark:text-text-dark-primary">
                {preflight.version.incoming ?? '—'}
                {preflight.version.current && <span className="ml-1 text-text-tertiary">(now {preflight.version.current})</span>}
              </dd>
              <dt className="text-text-secondary dark:text-text-dark-secondary">Coverage</dt>
              <dd className="text-text-primary dark:text-text-dark-primary">
                {coverageLabel(preflight.coverage.incoming)}
                {preflight.coverage.contest?.length ? (
                  <span className="ml-1 text-text-tertiary">(race: {preflight.coverage.contest.map(coverageLabel).join(' / ')})</span>
                ) : null}
              </dd>
              <dt className="text-text-secondary dark:text-text-dark-secondary">In their own words</dt>
              <dd className="text-text-primary dark:text-text-dark-primary">
                {preflight.statements.incoming} row{preflight.statements.incoming === 1 ? '' : 's'}
                {preflight.statements.sources.length ? ` from ${preflight.statements.sources.join(', ')}` : ''}
                <span className="ml-1 text-text-tertiary">(now {preflight.statements.current})</span>
              </dd>
              <dt className="text-text-secondary dark:text-text-dark-secondary">Endorsements</dt>
              <dd className="text-text-primary dark:text-text-dark-primary">
                {preflight.endorsements.incoming} <span className="ml-1 text-text-tertiary">(now {preflight.endorsements.current})</span>
              </dd>
              <dt className="text-text-secondary dark:text-text-dark-secondary">Measure positions</dt>
              <dd className="text-text-primary dark:text-text-dark-primary">
                {preflight.positions.resolved} of {preflight.positions.expected} statewide measures
                {preflight.positions.unresolved.length ? (
                  <span className="ml-1 text-warning-700 dark:text-warning-300">· unmatched: {preflight.positions.unresolved.join(', ')}</span>
                ) : null}
              </dd>
              {preflight.running_mate && (
                <>
                  <dt className="text-text-secondary dark:text-text-dark-secondary">Running mate</dt>
                  <dd className="text-text-primary dark:text-text-dark-primary">
                    {preflight.running_mate.incoming?.name ?? <span className="text-warning-700 dark:text-warning-300">{preflight.running_mate.problem}</span>}
                  </dd>
                </>
              )}
              <dt className="text-text-secondary dark:text-text-dark-secondary">Documents</dt>
              <dd className="text-text-primary dark:text-text-dark-primary">{preflight.documents.length ? preflight.documents.join(', ') : 'none'}</dd>
            </dl>

            <div>
              <p className={labelClasses}>Fields — untick Replace to keep what is there</p>
              <ul className="divide-y divide-border-light rounded-lg border border-border-light dark:divide-border-dark dark:border-border-dark">
                {preflight.fields.map((field) => (
                  <li key={field.column} className="grid grid-cols-[auto_1fr] gap-3 px-3 py-2 text-sm">
                    <label className="flex items-start gap-2 pt-0.5">
                      <input
                        type="checkbox"
                        checked={field.action !== 'keep'}
                        disabled={field.action === 'empty'}
                        onChange={() => toggleKeep(field.column)}
                        aria-label={`Replace ${field.label}`}
                      />
                    </label>
                    <div className="min-w-0">
                      <div className="font-medium text-text-primary dark:text-text-dark-primary">
                        {field.label}
                        {field.hand_edited && (
                          <span className="ml-2 text-xs font-normal text-warning-700 dark:text-warning-300">Staff-written now</span>
                        )}
                      </div>
                      <div className="truncate text-xs text-text-tertiary dark:text-text-dark-tertiary">
                        Now: {field.current ?? '—'}
                      </div>
                      <div className="truncate text-xs text-text-secondary dark:text-text-dark-secondary">
                        {field.action === 'keep' ? 'Keeping current' : field.action === 'clear' ? 'Will be cleared (file is empty)' : field.action === 'empty' ? 'Empty in both' : `New: ${field.incoming}`}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {preflight.ok && (
              <p className="flex items-center gap-2 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                <SuccessIcon className="h-4 w-4 text-success-600" />
                Every imported field is declared AI-assisted, staff-reviewed, with you as reviewer. Importing never publishes.
              </p>
            )}
          </div>
        )}
      </div>
    </AdminModal>
  );
};

export default CandidateProfileImportModal;
